<template>
  <svg :width="width" :height="height">
    <g>
      <g class="axes" >
        <template v-for="i in levels" >
          <line
             v-for="j in axisCount"
             class="segment"
             :x1="axisX(j - 1, i)"
             :y1="axisY(j - 1, i)"
             :x2="axisX(j, i)"
             :y2="axisY(j, i)" >
          </line>
        </template>
        <text
          v-if="axisCount"
          v-for="i in levels"
          class="scale"
          :x="axisX(0, i)"
          :y="axisY(0, i)"
          dx="6" >
          {{ formatter(scale.invert(i)) }}
        </text>
        <line
           v-for="i in axisCount"
           class="axis"
           :x1="width / 2"
           :y1="height / 2"
           :x2="axisX(i, levels)"
           :y2="axisY(i, levels)" >
        </line>
        <template v-if="sorted.length" >
          <template v-if="labelMaxWidth > 0" >
            <ellipsis
               v-for="(v, i) in sorted[0].values"
               :key="i"
               @click="axeClick(v, i)"
               class="label"
               :text-anchor="labelAnchor(i)"
               :x="axisX(i, levels + 0.6)"
               :y="axisY(i, levels + 0.6)" 
               :width="labelMaxWidth"
               :text="name(v)" />
          </template>
          <template v-else >
            <text
               v-for="(v, i) in sorted[0].values"
               :key="i"
               @click="axeClick(v, i)"
               class="label"
               :text-anchor="labelAnchor(i)"
               :x="axisX(i, levels + 0.6)"
               :y="axisY(i, levels + 0.6)" >
              {{ name(v) }}
            </text>
          </template>
        </template>
      </g>
      <g v-for="(d, i) in sorted" >
        <polygon :points="points(d)" :style="style(d, i)" ></polygon>
        <circle
           v-for="(v, j) in d.values"
           v-if="value(v) >= domain[0] && value(v) <= domain[1]"
           :cx="axisX(j, scale(value(v)))"
           :cy="axisY(j, scale(value(v)))"
           :r="5"
           :style="style(d, i)" >
        </circle>
      </g>
      <g :transform="graphTransform">
        <template v-if="showLegend" >
          <draggable v-if="draggableLegend" ref="draggableLegend" >
            <graph-legend :keys="keyColors" :x="graphWidth - legendWidth" :width="legendWidth" >
            </graph-legend>
          </draggable>
          <graph-legend v-else :keys="keyColors" :x="graphWidth - legendWidth" :width="legendWidth" >
          </graph-legend>
        </template>
      </g>
    </g>
  </svg>
</template>

<style scoped>
.segment {
    stroke: grey;
    stroke-opacity: 0.75;
    stroke-width: 0.5px;
}
.scale {
    font-size: 10px;
}
.axis {
    stroke: grey;
    stroke-width: 1px;
}
polygon {
    stroke-width: 2px;
    fill-opacity: 0.3;
}
polygon:hover {
    fill-opacity: 0.6;
}
</style>

<script>
import * as d3 from 'd3'
import * as d3Chromatic from 'd3-scale-chromatic'
import Graph from './Graph.vue'
import margin from '../mixins/margin.js'
import DraggableLegend from '../mixins/draggable-legend.js'
import Ellipsis from './Ellipsis.vue'

export default {
  extends: Graph,
  mixins: [
    margin,
    DraggableLegend
  ],
  components: {
    Ellipsis: Ellipsis
  },
  props: {
    width: {
      type: Number,
      default: 500
    },
    height: {
      type: Number,
      default: 500
    },
    scaleFunc: {
      default: () => {
        return d3.scaleLinear()
      }
    },
    levels: {
      type: Number,
      default: 5
    },
    domain: {
      default: () => {
        return [0, 100]
      }
    },
    name: {
      default: () => {
        return d => {
          return d.name
        }
      }
    },
    value: {
      default: () => {
        return d => {
          return d.value
        }
      }
    },
    title: {
      default: () => {
        return d => {
          return d.title
        }
      }
    },
    colorScale: {
      default: () => {
        return d3.scaleSequential(d3Chromatic.interpolateRainbow)
      }
    },
    formatter: {
      type: Function,
      default: n => Number.parseFloat(n).toPrecision(5)
    },
    labelMaxWidth: {
      type: Number,
      default: 500
    }
  },
  computed: {
    _color () {
      const scale = this.colorScale.domain([0, this.sorted.length])
      return d3.scaleOrdinal(this.sorted.map((k, i) => scale(i))).domain(this.sorted.map((s, i) => i))
    },
    sorted () {
      return this.data.map(d => {
        const result = {}
        Object.keys(d).forEach(k => {
          if (k === 'values') {
            result[k] = d[k].map(v => v).sort((a, b) => {
              return this.name(a) < this.name(b)
            })
          } else {
            result[k] = d[k]
          }
        })
        return result
      })
    },
    scale () {
      return this.scaleFunc.domain(this.domain).range(this.range)
    },
    axisCount () {
      if (this.sorted.length) {
        return this.sorted[0].values.length
      }
      return 0
    },
    radius () {
      return Math.min(this.graphWidth, this.graphHeight) / 2
    },
    range () {
      return [0, this.levels]
    },
    keyColors () {
      return this.sorted.map((d, i) => {
        return {
          label: this.title(d),
          color: this.color(i)
        }
      })
    }
  },
  methods: {
    labelAnchor (i) {
      if (i === 0 || i === (this.sorted[0].values.length / 2)) {
        return 'middle'
      }
      if (i < (this.sorted[0].values.length / 2)) {
        return 'end'
      }
      return 'start'
    },
    axisX (i, j) {
      return this.width / 2 - Math.sin(i * 2 * Math.PI / this.axisCount)
        * this.radius * j / this.levels
    },
    axisY (i, j) {
      return this.height / 2 - Math.cos(i * 2 * Math.PI / this.axisCount)
        * this.radius * j / this.levels
    },
    points (data) {
      return data.values.filter(d => {
        return this.value(d) >= this.domain[0] && this.value(d) <= this.domain[1]
      }).map((d, i) => {
        const v = this.scale(this.value(d))
        return this.axisX(i, v) + ',' + this.axisY(i, v)
      }).join(' ')
    },
    style (d, i) {
      const color = this.color(i)
      return {
        stroke: color,
        fill: color
      }
    },
    axeClick (v, i) {
      this.$emit('axe-click', {
        axe: v,
        index: i
      })
    },
    color (i) {
      return this._color(i)
    }
  }
}
</script>



// WEBPACK FOOTER //
// node_modules/d3vue/components/Radar.vue